/* 0.47.28 - The Chip element: a short label on a colored chip, after
 * the Henaket tag (components/Tag.jsx): 4px corners, 8px side padding,
 * semibold 16 / 24 text, Blue 300 fill, so 32px tall at the default size.
 *
 * Element fields (all optional; empty is Henaket):
 *   text             the label (translated; may be {field} or =formula)
 *   chip_fill        fill: a Henaket token or '#RRGGBB'   (Blue 300)
 *   chip_text_color  text color  (Text 800, white on a dark fill)
 *   chip_stroke      a 1px outline inside the chip        (none)
 *   chip_font_size   12, 14, 16, 18, 20 or 24 px           (16)
 *   chip_weight      'regular' | 'semibold' | 'bold'       (semibold)
 *   chip_shape       '' (4px corners) | 'round' (a pill)
 *   chip_caps        true: all capitals
 *
 * A rule changes the text with set text on the chip's id, and its
 * colors with set text on <id>.fill_color, <id>.text_color and
 * <id>.stroke_color (the `colors` argument here). */
import { colorCss } from '../lib/colorCss';

export const CHIP_FONT_SIZES = [12, 14, 16, 18, 20, 24];
const LINE = { 12: 16, 14: 20, 16: 24, 18: 28, 20: 28, 24: 32 };
const WEIGHT = { regular: 'var(--font-weight-regular, 400)', semibold: 'var(--font-weight-semibold, 600)', bold: 'var(--font-weight-bold, 700)' };
const HENAKET_FILL = '#CBD6E3';

/* Whether white text reads better than near-black on this hex color
 * (WCAG contrast, as the Layer's text color). */
function isDark(hex) {
  let h = (hex || '').replace('#', '');
  if (!/^[0-9a-f]{6}$/i.test(h)) return false;
  const lin = (i) => { const c = parseInt(h.slice(i, i + 2), 16) / 255; return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4; };
  const L = 0.2126 * lin(0) + 0.7152 * lin(2) + 0.0722 * lin(4);
  return (1.05 / (L + 0.05)) > ((L + 0.05) / 0.0603);
}

function hexOf(v) {
  // A Henaket token is read from the page's CSS (so a theme counts).
  if (typeof v !== 'string') return '';
  if (/^#[0-9a-f]{6}$/i.test(v)) return v;
  if (/^--[\w-]+$/.test(v) && typeof document !== 'undefined') {
    const c = getComputedStyle(document.documentElement).getPropertyValue(v).trim();
    if (/^#[0-9a-f]{6}$/i.test(c)) return c;
    if (/^#[0-9a-f]{3}$/i.test(c)) return '#' + c.slice(1).split('').map(x => x + x).join('');
  }
  return '';
}

export function chipStyle(e, colors = {}) {
  const fill = colors.fill || e.chip_fill || '';
  const textC = colors.text || e.chip_text_color || '';
  const stroke = colors.stroke || e.chip_stroke || '';
  const size = CHIP_FONT_SIZES.includes(Number(e.chip_font_size)) ? Number(e.chip_font_size) : 16;
  const dark = !!fill && isDark(hexOf(fill));
  return {
    display: 'inline-flex',
    alignSelf: 'flex-start',
    width: 'fit-content',
    alignItems: 'center',
    boxSizing: 'border-box',
    maxWidth: '100%',
    padding: `4px ${size >= 20 ? 12 : 8}px`,
    borderRadius: e.chip_shape === 'round' ? '999px' : 'var(--radius-sm, 4px)',
    background: colorCss(fill) || HENAKET_FILL,
    color: colorCss(textC) || (dark ? 'var(--color-white, #fff)' : 'var(--color-text-800, #1a1a1a)'),
    boxShadow: colorCss(stroke) ? `inset 0 0 0 1px ${colorCss(stroke)}` : undefined,
    fontFamily: 'var(--font-family-base)',
    fontSize: `${size}px`,
    lineHeight: `${LINE[size]}px`,
    fontWeight: WEIGHT[e.chip_weight] || WEIGHT.semibold,
    textTransform: e.chip_caps ? 'uppercase' : undefined,
    overflowWrap: 'anywhere',
  };
}

export default function Chip({ e, text, colors }) {
  const t = text == null ? '' : String(text);
  if (t === '') return null;
  return <span data-chip="" style={chipStyle(e, colors)}>{t}</span>;
}
